import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useConfirmStockPackMutation,
  useMakeRfdStockPackMutation,
  useCancelStockPackMutation,
  useUnpackStockPackMutation,
} from "../../../../store/apiSlice/warehouseApiSlice";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ConfirmationModal } from "../../../../components/organisms/Modal/confirmationModal";
import { ButtonSpinner } from "../../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

/**
 * Stable key for comparing snapshotted shipping addresses across packs.
 *
 * @param {object|null|undefined} address Shipping address snapshot.
 * @returns {string}
 */
const getShippingAddressKey = address => {
  if (!address || typeof address !== "object") return "";
  if (address.formatted) return String(address.formatted).trim();
  return [address.street, address.city, address.state, address.post_code, address.country]
    .filter(Boolean)
    .join("|");
};

const StockPackAction = ({ selectedOrderId, setSelectedOrder, selectedOrderFromListview }) => {
  const navigate = useNavigate();
  const notify = useNotification();

  const [confirmStockPack, { isLoading: isConfirming } = {}] = useConfirmStockPackMutation();
  const [makeRfdStockPack, { isLoading: isMakingRfd } = {}] = useMakeRfdStockPackMutation();
  const [cancelStockPack, { isLoading: isCancellingPack } = {}] = useCancelStockPackMutation();
  const [unpackStockPack, { isLoading: isUnpacking } = {}] = useUnpackStockPackMutation();

  const [isCancelling, setIsCancelling] = useState(false);

  const handleConfirmPack = async () => {
    try {
      await confirmStockPack({ stock_pack_ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message:
          selectedOrderId.length === 1
            ? `Stock Pack “${selectedOrderFromListview[0]?.name || selectedOrderId[0]}” confirmed successfully.`
            : `${selectedOrderId.length} Stock Packs confirmed successfully.`,
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({ type: "error", message: error.data?.detail || "Failed to confirm stock pack(s)." });
    }
  };

  const handleMakeRfd = async () => {
    try {
      await makeRfdStockPack({ stock_pack_ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message: "Stock Pack(s) marked as ready for dispatch successfully.",
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error.data?.detail || "Failed to mark ready for dispatch.",
      });
    }
  };

  const handleUnpack = async () => {
    try {
      await unpackStockPack({ stock_pack_ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message: "Items unpacked and returned to ready to pack.",
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error.data?.detail || "Failed to unpack stock pack(s).",
      });
    }
  };

  const handleCancelSubmit = async () => {
    try {
      await cancelStockPack({ stock_pack_ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message:
          selectedOrderId.length === 1
            ? `Stock Pack “${selectedOrderFromListview[0]?.name || selectedOrderId[0]}” cancelled successfully.`
            : `${selectedOrderId.length} Stock Packs cancelled successfully.`,
      });
      setSelectedOrder([]);
      setIsCancelling(false);
    } catch (error) {
      notify({ type: "error", message: error.data?.detail || "Failed to cancel stock pack(s)." });
    }
  };

  const hasSelection = selectedOrderId.length > 0;

  const canConfirmPack = useMemo(() => {
    return hasSelection && selectedOrderFromListview.every(item => item?.status?.id === "created");
  }, [hasSelection, selectedOrderFromListview]);

  const canMakeRfd = useMemo(() => {
    return (
      hasSelection &&
      selectedOrderFromListview.every(
        item => item?.status?.id === "fully_packed" || item?.status?.id === "packed",
      )
    );
  }, [hasSelection, selectedOrderFromListview]);

  const canUnpack = useMemo(() => {
    return (
      hasSelection &&
      selectedOrderFromListview.every(
        item => item?.status?.id === "fully_packed" || item?.status?.id === "packed",
      )
    );
  }, [hasSelection, selectedOrderFromListview]);

  const canCancel = useMemo(() => {
    return (
      hasSelection && selectedOrderFromListview.every(item => item?.status?.id !== "cancelled")
    );
  }, [hasSelection, selectedOrderFromListview]);

  const canCreateDelivery = useMemo(() => {
    if (!hasSelection) return false;

    // Condition 1: fully_rfd status
    const isFullyRfd = selectedOrderFromListview.every(item => item?.status?.id === "fully_rfd");
    if (!isFullyRfd) return false;

    // Condition 2: Not manifested on an active delivery line.
    const isUnmanifested = selectedOrderFromListview.every(
      item => !item?.active_delivery_line && !item?.active_delivery_line_id && !item?.delivery_line,
    );

    // Condition 3: Same warehouse, customer, and shipping address snapshot.
    const firstItem = selectedOrderFromListview[0];
    const warehouseId = firstItem?.warehouse?.id;
    const customerId = firstItem?.customer?.id;
    const shippingAddressKey = getShippingAddressKey(firstItem?.shipping_address);

    const isConsistent = selectedOrderFromListview.every(
      item =>
        item?.warehouse?.id === warehouseId &&
        item?.customer?.id === customerId &&
        getShippingAddressKey(item?.shipping_address) === shippingAddressKey,
    );

    return isFullyRfd && isUnmanifested && isConsistent;
  }, [hasSelection, selectedOrderFromListview]);

  const handleCreateDelivery = () => {
    const firstPack = selectedOrderFromListview[0];
    navigate("/delivery/create", {
      state: {
        stock_pack_ids: selectedOrderId,
        warehouse: firstPack?.warehouse,
        customer: firstPack?.customer,
        shipping_address: firstPack?.shipping_address,
      },
    });
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "unpack",
        label: "Unpack",
        onClick: handleUnpack,
        disabled: !canUnpack || isUnpacking,
        title: !canUnpack && hasSelection ? "Only allowed for packs in PACKED status" : !hasSelection ? "Select at least one packed pack." : "",
        isLoading: isUnpacking,
      },
      {
        key: "cancel",
        label: "Cancel",
        onClick: () => setIsCancelling(true),
        disabled: !canCancel || isCancellingPack,
        title: !canCancel && hasSelection ? "Cannot cancel an already cancelled pack" : !hasSelection ? "Select at least one pack to cancel." : "",
        isLoading: isCancellingPack,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canUnpack, isUnpacking, canCancel, isCancellingPack, hasSelection, selectedOrderId],
  );

  return (
    <>
      <li className={`tableButton ${canConfirmPack && !isConfirming ? "activeState" : "disableState"}`}>
        <button
          onClick={handleConfirmPack}
          disabled={!canConfirmPack || isConfirming}
          title={!canConfirmPack && hasSelection ? "Only allowed for packs in CREATED status" : !hasSelection ? "Select at least one pack in CREATED status." : ""}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isConfirming && <ButtonSpinner aria-hidden="true" />}
          {isConfirming ? "Confirming…" : "Confirm Pack"}
        </button>
      </li>
      <li className={`tableButton ${canMakeRfd && !isMakingRfd ? "activeState" : "disableState"}`}>
        <button
          onClick={handleMakeRfd}
          disabled={!canMakeRfd || isMakingRfd}
          title={!canMakeRfd && hasSelection ? "Only allowed for packs in FULLY_PACKED status" : !hasSelection ? "Select at least one pack in FULLY_PACKED status." : ""}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isMakingRfd && <ButtonSpinner aria-hidden="true" />}
          {isMakingRfd ? "Updating…" : "Ready for Dispatch"}
        </button>
      </li>
      <li className={`tableButton ${canCreateDelivery ? "activeState" : "disableState"}`}>
        <button
          onClick={handleCreateDelivery}
          disabled={!canCreateDelivery}
          title={
            !canCreateDelivery && hasSelection
              ? "All packs must be FULLY_RFD, unmanifested, and share the same warehouse, customer, and shipping address"
              : !hasSelection
                ? "Select at least one pack ready for dispatch."
                : ""
          }
        >
          Create Delivery
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCancelling && (
        <ConfirmationModal
          titleOfModal="Cancel Stock Pack"
          btnName="Cancel Stock Pack"
          closeModal={() => {
            setIsCancelling(false);
          }}
          handleModalWork={handleCancelSubmit}
          isLoading={isCancellingPack}
          description={<>Are you sure you want to cancel the selected stock pack(s)?</>}
        />
      )}
    </>
  );
};

export const StockPackActionWithCheck = componentWithCondition(StockPackAction);
